import NumericTextField from 'components/NumericTextField';
import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment-timezone';
import { PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { ChoiceGroup } from 'office-ui-fabric-react/lib/ChoiceGroup';
import DatePicker from 'components/DatePicker';
import {
  Dialog,
  DialogFooter,
  DialogType,
} from 'office-ui-fabric-react/lib/Dialog';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import React, { Component, Fragment } from 'react';
import { MessageBar } from 'office-ui-fabric-react';
import CustomCalendar from './Calendar';

@inject('app', 'sale')
@observer
class RoundDurationEditDefault extends Component {
  @observable changedFields = {};
  @observable sendNotification = false;

  handleSelectNow = (field) => (value) => {
    if (!value) return;
    const {
      store: { round },
    } = this.props;
    round[field] = value;
    this.changedFields[field] = true;
    this.ensureRoundEnd(field, value);
  };

  handleFieldChanged = (field) => (value) => {
    const {
      store: { round },
    } = this.props;

    let h = 12;
    let m = 0;
    if (moment(round[field]).isValid()) {
      h = moment(round[field]).hours();
      m = moment(round[field]).minutes();
    }

    if (value instanceof Date) value = moment(value).hour(h).minute(m).toDate();

    round[field] = value;
    this.changedFields[field] = true;
    this.ensureRoundEnd(field, value);
  };

  handleDurationTypeChanged = (_, opt) => {
    const {
      store: { round },
    } = this.props;
    round.durationType = opt.key;
  };

  handleTimeChange = (field) => (value) => {
    const {
      store: { round },
    } = this.props;
    const duration = value.key;

    const h = moment.duration(duration, 'minutes').hours();
    const m = moment.duration(duration, 'minutes').minutes();
    round[field] = moment(round[field]).hour(h).minute(m).toDate();
    this.ensureRoundEnd(field, value);
  };

  ensureRoundEnd = (field, value) => {
    const {
      store: { round },
      sale,
    } = this.props;

    const config = sale.saleConfig;

    if (field !== 'endsAt') return;
    if (!this.changedFields['offerValidityUntil'])
      round.offerValidityUntil = moment(round.endsAt)
        .add(config.roundDefaultOfferValidityExtensionInDays, 'd')
        .toDate();
    const oldValue = moment(round.endsAt).toDate();
    this.sendNotification =
      round.status === 'opened' &&
      !moment(oldValue).isSame(moment(this.endsAt));
  };

  render() {
    const {
      store: { round },
      t,
      canChangeDurationType,
      sale,
    } = this.props;

    const isSimpleOffer = round.type === 'simple_offer';

    const config = sale.saleConfig;
    const durationTypes = (t) =>
      config.durationTypes.map((key) => {
        return { key: key, text: t(`durationtype::${key}`) };
      });

    return (
      <Fragment>
        {this.openRoundWarning && this.renderWarning()}
        {canChangeDurationType && (
          <div>
            <ChoiceGroup
              defaultSelectedKey={round.durationType}
              options={durationTypes(t)}
              onChange={this.handleDurationTypeChanged}
              // label={t('Duration type')}
              label={isSimpleOffer ? t('Offer validity') : t('Duration type')}
            />
          </div>
        )}
        {round.durationType === 'flexible'
          ? this.renderFlexibleDurationTypeForm()
          : this.renderFixedDurationTypeForm()}
      </Fragment>
    );
  }

  renderWarning = () => {
    const { t } = this.props;
    const close = () => (this.openRoundWarning = false);
    return (
      <Dialog
        hidden={false}
        onDismiss={close}
        dialogContentProps={{
          type: DialogType.close,
        }}
        modalProps={{
          titleAriaId: 'myLabelId',
          subtitleAriaId: 'mySubTextId',
          isBlocking: true,
          containerClassName: 'ms-dialogMainOverride',
        }}
      >
        {t('open round warning')}
        <DialogFooter>
          <PrimaryButton onClick={close} text={t('Ok')} />
        </DialogFooter>
      </Dialog>
    );
  };

  renderFlexibleDurationTypeForm = () => {
    const {
      store: { round },
      t,
      hideValidity,
    } = this.props;
    const isSimpleOffer = round.type === 'simple_offer';
    return (
      <div>
        {isSimpleOffer ? null : (
          <div>
            <NumericTextField
              label={t('Duration')}
              suffix={t('Days')}
              value={round.durationInDays}
              locale={this.props.intl.locale}
              onChanged={this.handleFieldChanged('durationInDays')}
              required={true}
              defaultValue={0}
              autoComplete="off"
            />
          </div>
        )}
        {!hideValidity && (
          <Fragment>
            <div>
              <NumericTextField
                label={isSimpleOffer ? t('duration') : t('offer validity')}
                suffix={t('Days')}
                value={round.offerValidityInDays || 0}
                locale={this.props.intl.locale}
                onChanged={this.handleFieldChanged('offerValidityInDays')}
                required={true}
                defaultValue={0}
                autoComplete="off"
              />
            </div>
            {isSimpleOffer && (
              <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-sm12">
                  <MessageBar>
                    {t('Simple offer duration suggestion notice')}
                  </MessageBar>
                </div>
              </div>
            )}
          </Fragment>
        )}
      </div>
    );
  };

  renderFixedDurationTypeForm = () => {
    const {
      store: { round },
      sale,
      t,
      hideValidity,
    } = this.props;

    const momentData = moment.localeData();
    const timezone = sale.saleEdit.offerData.timezone;
    const probeDate =
      round.endsAt || round.startsAt || round.offerValidityUntil || new Date();
    const isInTimezone =
      !timezone ||
      moment(probeDate).format('Z') ===
        moment(probeDate).tz(timezone).format('Z');
    const timeZoneSuffix = !isInTimezone
      ? ` (${moment.tz(timezone).format('z')})`
      : '';

    const isSimpleOffer = round.type === 'simple_offer';

    return (
      <div className="ms-Grid" style={{ padding: 0 }}>
        {isSimpleOffer ? null : (
          <Fragment>
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-sm8">
                <DatePicker
                  calendarAs={CustomCalendar}
                  calendarProps={{
                    t,
                    onClickNow: this.handleSelectNow('startsAt'),
                    timezone,
                  }}
                  label={t('Start')}
                  tooltip={t('Start - tooltip')}
                  isRequired={false}
                  value={round.startsAt}
                  onSelectDate={this.handleFieldChanged('startsAt')}
                  allowTextInput={true}
                  // maxDate={round.endsAt}
                  strings={{
                    months: momentData._months,
                    shortMonths: momentData._monthsShort,
                    days: momentData._weekdays,
                    shortDays: momentData._weekdaysMin,
                    goToToday: t('Start Now'),
                  }}
                  showGoToToday={false}
                />
              </div>
              <div className="ms-Grid-col ms-sm4">
                <TimeSelect
                  value={duration(round.startsAt)}
                  onChanged={this.handleTimeChange('startsAt')}
                  label={`${t('Time of day')}${timeZoneSuffix}`}
                  momentData={momentData}
                />
              </div>
            </div>
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-sm8">
                <DatePicker
                  label={t('Ends at')}
                  tooltip={t('Ends at - tooltip')}
                  isRequired={true}
                  firstDayOfWeek={momentData.firstDayOfWeek()}
                  value={round.endsAt}
                  formatDate={this.renderFormattedDate}
                  onSelectDate={this.handleFieldChanged('endsAt')}
                  minDate={round.startsAt}
                  // maxDate={
                  //   this.changedFields['offerValidityUntil']
                  //     ? round.offerValidityUntil
                  //     : null
                  // }
                  strings={{
                    months: momentData._months,
                    shortMonths: momentData._monthsShort,
                    days: momentData._weekdays,
                    shortDays: momentData._weekdaysMin,
                  }}
                  showGoToToday={false}
                />
              </div>
              <div className="ms-Grid-col ms-sm4">
                <TimeSelect
                  value={duration(round.endsAt)}
                  onChanged={this.handleTimeChange('endsAt')}
                  label={`${t('Time of day')}${timeZoneSuffix}`}
                  momentData={momentData}
                />
              </div>
            </div>
            {(round.type === 'open_bidding' ||
              round.type === 'incremental_auction') && (
              <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-sm12">
                  <MessageBar>{t('Antisniping notice')}</MessageBar>
                </div>
              </div>
            )}
          </Fragment>
        )}
        {this.sendNotification && (
          <div className="ms-Grid-row">
            <div
              className="ms-Grid-col ms-sm12"
              style={{ paddingTop: '10px', paddingBottom: '10px' }}
            >
              <span style={{ color: '#a80000' }}>{t('End date changed')}</span>
            </div>
          </div>
        )}
        {!hideValidity && (
          <Fragment>
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-sm8">
                <DatePicker
                  label={isSimpleOffer ? t('date') : t('offer validity')}
                  isRequired={true}
                  firstDayOfWeek={momentData.firstDayOfWeek()}
                  {...(!!round.offerValidityUntil
                    ? { value: round.offerValidityUntil }
                    : {})}
                  formatDate={this.renderFormattedDate}
                  onSelectDate={this.handleFieldChanged('offerValidityUntil')}
                  minDate={round.endsAt}
                  strings={{
                    months: momentData._months,
                    shortMonths: momentData._monthsShort,
                    days: momentData._weekdays,
                    shortDays: momentData._weekdaysMin,
                  }}
                  showGoToToday={false}
                />
              </div>
              <div className="ms-Grid-col ms-sm4">
                <TimeSelect
                  value={duration(round.offerValidityUntil)}
                  onChanged={this.handleTimeChange('offerValidityUntil')}
                  label={`${t('Time of day')}${timeZoneSuffix}`}
                  momentData={momentData}
                />
              </div>
            </div>
            {isSimpleOffer && (
              <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-sm12">
                  <MessageBar>
                    {t('Simple offer duration suggestion notice')}
                  </MessageBar>
                </div>
              </div>
            )}
          </Fragment>
        )}
      </div>
    );
  };

  renderFormattedDate = (date) => {
    return date.toString().toFormatedDateString();
  };
}

const duration = (date) => {
  return moment.duration(moment(date).format('HH:mm')).asMinutes();
};

const timeOptions = (momentData) => {
  const format = momentData.longDateFormat('LT');
  return [...Array(24 * 4).keys()].map((v) => {
    return {
      key: v * 15,
      text: moment('2020-01-01')
        .add(v * 15, 'minutes')
        .format(format),
    };
  });
};

const TimeSelect = ({ label, onChanged, value, momentData }) => {
  return (
    <Dropdown
      label={label}
      ariaLabel={label}
      selectedKey={value}
      options={timeOptions(momentData)}
      isRequired={true}
      onChanged={onChanged}
    />
  );
};

export default RoundDurationEditDefault;



// WEBPACK FOOTER //
// ./src/app/containers/Sale/RoundSection/RoundDurationEditDefault.jsx